import React, { useMemo } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  Calendar, 
  Award, 
  CheckCircle,
  Clock
} from 'lucide-react';

const AnalyticsDashboard = ({ stats, notaries }) => {
  // Prepare data for charts
  const verificationData = [
    { name: 'Vérifiés', value: stats?.verified || 0, color: '#10B981' },
    { name: 'En attente', value: stats?.pending || 0, color: '#F59E0B' }
  ];

  const specializationData = React.useMemo(() => {
    const specializationCount = {};
    
    notaries?.forEach(notary => {
      notary?.specializations?.forEach(spec => {
        specializationCount[spec] = (specializationCount?.[spec] || 0) + 1;
      });
    });

    return Object.entries(specializationCount)?.map(([name, value]) => ({ name, value }))?.sort((a, b) => b?.value - a?.value)?.slice(0, 10);
  }, [notaries]);

  const monthlyData = [
    { month: 'Jan', notaires: 2, appointments: 15 },
    { month: 'Fév', notaires: 3, appointments: 22 },
    { month: 'Mar', notaires: 1, appointments: 28 },
    { month: 'Avr', notaires: 4, appointments: 35 },
    { month: 'Mai', notaires: 2, appointments: 42 },
    { month: 'Jun', notaires: 3, appointments: 38 }
  ];

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Notaires</p>
              <p className="text-2xl font-semibold text-gray-900">{stats?.total || 0}</p>
              <p className="text-xs text-green-600 flex items-center mt-1">
                <TrendingUp className="w-3 h-3 mr-1" />
                +12% ce mois
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Taux de Vérification</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats?.total > 0 ? Math.round((stats?.verified / stats?.total) * 100) : 0}%
              </p>
              <p className="text-xs text-green-600 flex items-center mt-1">
                <TrendingUp className="w-3 h-3 mr-1" />
                +5% ce mois
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">RDV ce mois</p>
              <p className="text-2xl font-semibold text-gray-900">{stats?.appointmentsThisMonth || 0}</p>
              <p className="text-xs text-green-600 flex items-center mt-1">
                <TrendingUp className="w-3 h-3 mr-1" />
                +18% ce mois
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Award className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Spécialisations</p>
              <p className="text-2xl font-semibold text-gray-900">{specializationData?.length || 0}</p>
              <p className="text-xs text-gray-600 mt-1">
                Types différents
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Verification Status Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Statut de Vérification
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={verificationData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {verificationData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [value, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center space-x-4 mt-4">
            {verificationData?.map((item) => (
              <div key={item?.name} className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: item?.color }}
                />
                <span className="text-sm text-gray-600">
                  {item?.name}: {item?.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Specializations */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Spécialisations Principales
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={specializationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  fontSize={12}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Monthly Trends */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Évolution Mensuelle
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="notaires" 
                stroke="#3B82F6" 
                strokeWidth={2}
                name="Nouveaux Notaires"
              />
              <Line 
                type="monotone" 
                dataKey="appointments" 
                stroke="#10B981" 
                strokeWidth={2}
                name="Rendez-vous"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Activité Récente
        </h3>
        <div className="space-y-3">
          {notaries?.slice(0, 5)?.map((notary) => (
            <div key={notary?.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {notary?.user_profile?.full_name || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Licence: {notary?.license_number}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {notary?.is_verified ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <Clock className="w-4 h-4 text-yellow-500" />
                )}
                <span className="text-xs text-gray-500">
                  {new Date(notary?.created_at)?.toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;